import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Datagrid, DateField, FunctionField, ReferenceField, SelectField, TextField, usePermissions, useRecordContext, useTranslate } from 'react-admin';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import SimpleList from '@/components/global/list/SimpleList.jsx';
import { CustomDatagridBody } from '@/components/global/list/CustomDatagridRow.jsx';
import NotificationBox from '@/components/global/fields/NotificationBox.jsx';
import { Palette } from '@/themes/themeOverrides/Palette';
import { operatorStatusChoices } from '@/components/global/options/selectOptions.jsx';

const StyledTransactionHistory = styled(Box)(() => ({
    width: '100%',
    '& .transaction-history-payloads': {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '16px',
        padding: '20px 24px 24px',
        backgroundColor: Palette.gray.gray75,
        '& pre': {
            maxHeight: '420px',
            margin: '8px 0 0',
            padding: '16px',
            overflow: 'auto',
            borderRadius: '8px',
            backgroundColor: Palette.white,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
        },
    },
}));

const formatPayload = payload => {
    if (payload === null || payload === undefined || payload === '') {
        return '—';
    }

    if (typeof payload === 'string') {
        try {
            return JSON.stringify(JSON.parse(payload), null, 2);
        } catch {
            return payload;
        }
    }

    try {
        return JSON.stringify(payload, null, 2) ?? String(payload);
    } catch {
        return String(payload);
    }
};

const HistoryPayloadDetails = () => {
    const translate = useTranslate();
    const record = useRecordContext();

    if (!record) return null;

    return (
        <Box className='transaction-history-payloads'>
            <Box>
                <Typography variant='subtitle2'>{translate('resources.transaction.history.request')}</Typography>
                <pre>{formatPayload(record.request)}</pre>
            </Box>
            <Box>
                <Typography variant='subtitle2'>{translate('resources.transaction.history.response')}</Typography>
                <pre>{formatPayload(record.response)}</pre>
            </Box>
        </Box>
    );
};

export const TransactionHistory = ({ elementId, handleClose }) => {
    const translate = useTranslate();
    const { isLoading, permissions } = usePermissions();

    if (isLoading || !permissions?.isAdmin) return null;

    return (
        <StyledTransactionHistory>
            <Box className='popup-wrapper popup-wrapper-top'>
                <Box className='popup-box popup-box--header'>
                    <Typography variant='h4'>{translate('resources.transaction.history.title')}</Typography>
                    <CustomCloseButton onClose={handleClose} />
                </Box>
            </Box>
            <Box className='popup-wrapper'>
                <SimpleList
                    resource={`transaction/${elementId}/history`}
                    sort={{ field: 'timestamp', order: 'DESC' }}
                    storeKey={`transaction-history-${elementId}`}
                    perPage={50}
                    disablePagination
                    empty={
                        <NotificationBox variant='info' className='large'>
                            {translate('resources.transaction.history.noHistory')}
                        </NotificationBox>
                    }
                >
                    <Datagrid
                        body={<CustomDatagridBody />}
                        bulkActionButtons={false}
                        sort={false}
                        rowClick='expand'
                        expandSingle
                        expand={<HistoryPayloadDetails />}
                    >
                        <FunctionField
                            source='type'
                            label={translate('resources.transaction.history.type')}
                            sortable={false}
                            render={record =>
                                translate(`resources.transaction.history.types.${record.type}`, {
                                    _: record.type ?? '—',
                                })
                            }
                        />
                        <TextField source='forwarder' label={translate('resources.transaction.history.forwarder')} sortable={false} />
                        <SelectField
                            source='status'
                            label={translate('resources.transaction.history.status')}
                            choices={operatorStatusChoices(translate).map(choice => ({ id: choice.id, name: choice.name }))}
                            sortable={false}
                        />
                        <DateField source='timestamp' label={translate('resources.transaction.history.timestamp')} showTime sortable={false} />
                        <ReferenceField
                            source='initiatorId'
                            reference='user'
                            label={translate('resources.transaction.history.initiator')}
                            sortable={false}
                            link={false}
                            emptyText={translate('resources.transaction.history.system')}
                        >
                            <FunctionField render={record => `${record.firstName} ${record.lastName} (${record.email})`} />
                        </ReferenceField>
                    </Datagrid>
                </SimpleList>
            </Box>
        </StyledTransactionHistory>
    );
};

TransactionHistory.propTypes = {
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    handleClose: PropTypes.func.isRequired,
};
